// Pure drop-resolution — the prototype's cross-widget verbs, decided from a
// target widget's capabilities (no DOM/React). `resolveDrop` says WHAT a drop
// does; `mergePlayers` computes the resulting player list for a merge. Both are
// pure so the interaction model is pinned in tests, and the React drag is just
// the view that calls them.

import type { PlayerRef } from "@catbento/shared";
import { refKey } from "./playerRef.js";

/** What a drop target can do with a dropped player. Comes from the widget's
 *  registry entry. */
export interface DropTargetCaps {
  /** Accepts a second+ player into a comparison (stats cards, radar). */
  mergeable: boolean;
  /** Single-player, but a drop REPLACES its player (Matches, the maps). */
  replaceOnDrop: boolean;
}

/** The decided action. `none` = the target rejects the drop (e.g. Bio, which is
 *  changed via its own search, never by a drop). */
export type DropAction = "merge" | "replace" | "create-bio" | "none";

/**
 * Resolve a player drop. `null` target = dropped on empty canvas → a new Bio for
 * that player (the prototype's "drag a player into blank space"). Otherwise the
 * target's capabilities decide: mergeable → join the comparison; replace-on-drop
 * → swap its player; else the drop is rejected.
 */
export function resolveDrop(target: DropTargetCaps | null): DropAction {
  if (!target) return "create-bio";
  if (target.mergeable) return "merge";
  if (target.replaceOnDrop) return "replace";
  return "none";
}

/**
 * Add a dragged player to a mergeable widget's comparison. Dedup is on the full
 * 3-id ref (`refKey`), so the SAME player on a DIFFERENT season IS added (the
 * prototype allows comparing 24/25 vs 25/26); an exact-same ref is a no-op. Caps
 * at `maxPlayers`. Never mutates the source list (drag copies, not moves).
 * `added` lets the caller skip a no-op (a dedup or a full widget) so it doesn't
 * churn undo/dirty.
 */
export function mergePlayers(
  existing: PlayerRef[],
  incoming: PlayerRef,
  maxPlayers: number
): { players: PlayerRef[]; added: boolean } {
  if (existing.some((p) => refKey(p) === refKey(incoming))) {
    return { players: existing, added: false };
  }
  if (existing.length >= maxPlayers) {
    return { players: existing, added: false };
  }
  return { players: [...existing, incoming], added: true };
}
